import { booleanize } from '@utilities';
import {
  AdminBulkActionsService,
  AdminNotificationsService,
} from '../services/general';
import type {
  BulkActionFilters,
  BulkActionJobStatusResponse,
  BulkActionSubmitResponse,
  BulkActionTargetsResponse,
  NotificationResponse,
  PaginatedNotificationsResponse,
} from '../services/general';
import type { GetContactsFilters } from './contacts';

export type BulkActionType =
  | 'transfer_state'
  | 'start_campaign'
  | 'delete'
  | 'clean';

export type BulkActionStatus =
  | 'pending'
  | 'resolving'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export type BulkActionSelection =
  | {
      contact_ids: string[];
      filters?: never;
      deselected_ids?: never;
    }
  | {
      filters: BulkActionFilters;
      deselected_ids?: string[];
      contact_ids?: never;
    };

type TransferStateAction = {
  action_type: 'transfer_state';
  params: { target_state: string; reason?: string | null };
};

type ParameterlessAction = {
  action_type: Exclude<BulkActionType, 'transfer_state'>;
  params?: Record<string, never>;
};

export type SubmitBulkActionPayload = BulkActionSelection &
  (TransferStateAction | ParameterlessAction);

export const mapContactFiltersToBulkActionFilters = (
  filters: GetContactsFilters = {}
): BulkActionFilters => ({
  campaign_id: filters.campaignId || undefined,
  search: filters.search || undefined,
  latest_contact_state: filters.latest_contact_state || undefined,
  requires_human_input: booleanize(filters.requiresHumanInput),
  created_from: filters.createdFrom || undefined,
  created_to: filters.createdTo || undefined,
  updated_from: filters.updatedFrom || undefined,
  updated_to: filters.updatedTo || undefined,
});

export const submitBulkAction = (
  payload: SubmitBulkActionPayload
): Promise<BulkActionSubmitResponse> =>
  AdminBulkActionsService.submitBulkActionApiAdminBulkActionsPost(payload);

export const getBulkAction = (
  jobId: string
): Promise<BulkActionJobStatusResponse> =>
  AdminBulkActionsService.getBulkActionApiAdminBulkActionsJobIdGet(jobId);

export const getFailedBulkActionTargets = (
  jobId: string,
  limit = 50,
  offset = 0
): Promise<BulkActionTargetsResponse> =>
  AdminBulkActionsService.getBulkActionTargetsApiAdminBulkActionsJobIdTargetsGet(
    jobId,
    'failed',
    limit,
    offset
  );

export const getNotifications = (
  limit = 20,
  offset = 0
): Promise<PaginatedNotificationsResponse> =>
  AdminNotificationsService.getNotificationsApiAdminNotificationsGet(
    limit,
    offset
  );

export type {
  BulkActionFilters,
  BulkActionJobStatusResponse,
  BulkActionSubmitResponse,
  BulkActionTargetsResponse,
  NotificationResponse,
  PaginatedNotificationsResponse,
};
